<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: MediaOverlay                                          |
| Section: components                                              |
| Purpose: Fullscreen media viewer with drag, zoom, and metadata.  |
+------------------------------------------------------------------+
-->
<template>
    <div v-if="isVisible" class="overlay" @click="closeOverlay">
        <div ref="dialog" class="overlay-dialog" role="dialog" aria-modal="true"
            aria-labelledby="media-overlay-title" @click.stop>
            <MediaOverlayHeader ref="header" :title="mediaTitle" :media-type="mediaType" :media-kind="mediaKind"
                :owner="mediaOwner"
                :linked-person-ids="linkedPersonIDs" :current-index="currentMediaIndex"
                :total-items="mediaList.length" heading-id="media-overlay-title"
                details-id="media-overlay-details" @close="closeOverlay" />

            <div ref="overlayContent" class="overlay-content" @mousedown="startDrag" @mousemove="onDrag"
                @mouseup="endDrag" @mouseleave="endDrag" @wheel="onWheelZoom">
                <button v-if="hasPrevious" type="button" class="nav-button prev-button"
                    :aria-label="$t('mediaOverlay.previous')" @click="navigatePrevious">
                    <img src="@/assets/icons/next-prev-btn.svg" alt="" aria-hidden="true">
                </button>
                <button v-if="hasNext" type="button" class="nav-button next-button"
                    :aria-label="$t('mediaOverlay.next')" @click="navigateNext">
                    <img src="@/assets/icons/next-prev-btn.svg" alt="" aria-hidden="true">
                </button>

                <div v-if="mediaType === 'image'" class="zoom-controls">
                    <button type="button" :aria-label="$t('mediaOverlay.zoomIn')" @click="zoomIn">
                        <img src="@/assets/icons/zoom_in_white.svg" alt="" aria-hidden="true">
                    </button>
                    <button type="button" :aria-label="$t('mediaOverlay.zoomOut')" @click="zoomOut">
                        <img src="@/assets/icons/zoom_out_white.svg" alt="" aria-hidden="true">
                    </button>
                </div>

                <img v-if="mediaType === 'image'" ref="mediaElement" class="mediaElement" :src="mediaUrl"
                    :alt="mediaTitle || $t('mediaOverlay.untitled')" draggable="false">
                <iframe v-else :src="documentViewerUrl" class="document-viewer"
                    :title="$t('mediaOverlay.viewerTitle', { title: mediaTitle || $t('mediaOverlay.untitled') })"></iframe>
            </div>
        </div>
    </div>
</template>

<script>
import MediaOverlayHeader from '@/components/MediaOverlayHeader.vue';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])'
].join(',');

export default {
    name: 'MediaOverlay',
    components: { MediaOverlayHeader },
    emits: ['close', 'navigate'],
    props: {
        isVisible: { type: Boolean, required: true },
        mediaUrl: { type: String, required: true },
        mediaType: { type: String, required: true },
        mediaKind: { type: String, default: 'unknown' },
        mediaTitle: { type: String, default: '' },
        mediaOwner: { type: String, default: '' },
        linkedPersonIDs: { type: Array, default: () => [] },
        mediaList: { type: Array, default: () => [] },
        currentMediaIndex: { type: Number, default: 0 },
        infiniteNavigation: { type: Boolean, default: false }
    },
    data() {
        return {
            scale: 1,
            isDragging: false,
            startX: 0,
            startY: 0,
            translateX: 0,
            translateY: 0,
            previouslyFocusedElement: null
        };
    },
    computed: {
        hasNext() {
            return this.infiniteNavigation
                ? this.mediaList.length > 1
                : this.currentMediaIndex < this.mediaList.length - 1;
        },
        hasPrevious() {
            return this.infiniteNavigation
                ? this.mediaList.length > 1
                : this.currentMediaIndex > 0;
        },
        isPdfDocument() {
            return this.mediaType !== 'image' && /\.pdf($|[?#])/i.test(this.mediaUrl || '');
        },
        documentViewerUrl() {
            if (!this.isPdfDocument) return this.mediaUrl;
            return `/tng-admin/pdfjs/web/viewer.html?file=${encodeURIComponent(this.mediaUrl)}`;
        }
    },
    watch: {
        isVisible(newValue) {
            if (newValue) {
                this.openDialog();
            } else {
                this.removeNoScrollClass();
                this.restoreFocus();
            }
        },
        mediaUrl() {
            this.resetTransform();
        }
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
        if (this.isVisible) this.openDialog();
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown);
        this.removeNoScrollClass();
        this.restoreFocus();
    },
    methods: {
        openDialog() {
            if (!this.previouslyFocusedElement) {
                this.previouslyFocusedElement = document.activeElement;
            }
            this.addNoScrollClass();
            this.$nextTick(() => this.$refs.header?.focusCloseButton());
        },
        closeOverlay() {
            this.$refs.header?.closeDetails({ restoreFocus: false });
            this.removeNoScrollClass();
            this.$emit('close');
        },
        restoreFocus() {
            const target = this.previouslyFocusedElement;
            if (target && document.contains(target) && typeof target.focus === 'function') {
                target.focus();
            }
            this.previouslyFocusedElement = null;
        },
        handleKeydown(event) {
            if (!this.isVisible) return;

            if (event.key === 'Escape') {
                event.preventDefault();
                if (!this.$refs.header?.closeDetails()) this.closeOverlay();
                return;
            }

            if (event.key === 'Tab') {
                this.trapFocus(event);
                return;
            }

            if (event.key === 'ArrowRight' && this.hasNext) {
                event.preventDefault();
                this.navigateNext();
            } else if (event.key === 'ArrowLeft' && this.hasPrevious) {
                event.preventDefault();
                this.navigatePrevious();
            }
        },
        trapFocus(event) {
            const focusableElements = [...(this.$refs.dialog?.querySelectorAll(FOCUSABLE_SELECTOR) || [])]
                .filter((element) => element.getClientRects().length > 0);
            if (!focusableElements.length) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;

            if (event.shiftKey && (activeElement === firstElement || !this.$refs.dialog.contains(activeElement))) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && (activeElement === lastElement || !this.$refs.dialog.contains(activeElement))) {
                event.preventDefault();
                firstElement.focus();
            }
        },
        startDrag(event) {
            if (this.mediaType !== 'image' || event.button !== 0 || event.target.closest('button')) return;
            this.isDragging = true;
            this.startX = event.clientX - this.translateX;
            this.startY = event.clientY - this.translateY;
        },
        onDrag(event) {
            if (!this.isDragging) return;
            this.translateX = event.clientX - this.startX;
            this.translateY = event.clientY - this.startY;
            this.updateTransform();
        },
        endDrag() {
            this.isDragging = false;
        },
        onWheelZoom(event) {
            if (this.mediaType !== 'image') return;
            event.preventDefault();
            this.adjustScale(event.deltaY * -0.001);
        },
        zoomIn() {
            this.adjustScale(0.1);
        },
        zoomOut() {
            this.adjustScale(-0.1);
        },
        adjustScale(change) {
            this.scale = Math.min(Math.max(this.scale + change, 0.5), 4);
            this.updateTransform();
        },
        updateTransform() {
            if (!this.$refs.mediaElement) return;
            this.$refs.mediaElement.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
        },
        resetTransform() {
            this.scale = 1;
            this.translateX = 0;
            this.translateY = 0;
            this.isDragging = false;
            this.$nextTick(() => this.updateTransform());
        },
        addNoScrollClass() {
            document.body.classList.add('no-scroll');
        },
        removeNoScrollClass() {
            document.body.classList.remove('no-scroll');
        },
        navigateNext() {
            if (!this.hasNext) return;
            const nextIndex = this.infiniteNavigation
                ? (this.currentMediaIndex + 1) % this.mediaList.length
                : this.currentMediaIndex + 1;
            this.$emit('navigate', nextIndex);
        },
        navigatePrevious() {
            if (!this.hasPrevious) return;
            const previousIndex = this.infiniteNavigation
                ? (this.currentMediaIndex - 1 + this.mediaList.length) % this.mediaList.length
                : this.currentMediaIndex - 1;
            this.$emit('navigate', previousIndex);
        }
    }
};
</script>

<style scoped>
.overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(29, 29, 29, 0.96);
}

.overlay-dialog {
    display: flex;
    flex-direction: column;
    width: calc(100% - 96px);
    height: calc(100% - 96px);
    overflow: visible;
    border: 2px solid rgba(255, 255, 255, 0.24);
    background: var(--brand-bg-4);
}

.overlay-content {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    min-height: 0;
    overflow: hidden;
    cursor: grab;
    user-select: none;
    -webkit-user-drag: none;
}

.overlay-content:active {
    cursor: grabbing;
}

.mediaElement {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    transform: translate(0, 0) scale(1);
}

.document-viewer {
    width: 100%;
    height: 100%;
    border: 0;
}

.zoom-controls {
    position: absolute;
    right: 24px;
    bottom: 24px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.zoom-controls button,
.nav-button {
    padding: 0;
    background: transparent;
    border: 0;
    cursor: pointer;
    transition: var(--transition-bounce);
}

.zoom-controls button {
    border-radius: var(--border-radius-pill);
}

.zoom-controls button:hover,
.zoom-controls button:focus-visible {
    box-shadow: 0 0 0 4px var(--brand-bg-1);
    outline: none;
}

.nav-button {
    position: absolute;
    top: 50%;
    z-index: 10;
    overflow: hidden;
    border-radius: 2px;
    transform: translateY(-50%);
}

.nav-button:hover,
.nav-button:focus-visible {
    box-shadow: 0 0 0 4px var(--brand-color-1);
    outline: none;
}

.prev-button {
    left: 20px;
}

.prev-button img {
    transform: rotate(180deg);
}

.next-button {
    right: 20px;
}

@media (max-width: 860px) {
    .overlay-dialog {
        width: 100%;
        height: 100%;
        border: 0;
    }

    .nav-button {
        width: 32px;
        height: 32px;
    }

    .next-button {
        right: 12px;
    }

    .prev-button {
        left: 12px;
    }
}
</style>
